import { Component, ElementRef, OnInit, Input, ViewChild } from '@angular/core';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { HtmlEditorComponent } from '@app/modules/shared/html-editor/html-editor.component';
import { SsModule } from '@app/models';
import { SharedUtilsService, SsModuleService } from '@app/services';
import { ModuleSearchSelectComponent } from '@app/modules/module-tools/module-search-select/module-search-select.component';

declare const SegmentedMessage: any;

@Component({
    selector: 'job-personalized-campaign-message-dialog',
    templateUrl: './job-personalized-campaign-message-dialog.component.html',
    styleUrls: ['./job-personalized-campaign-message-dialog.component.scss'],
    standalone: false
})
export class JobPersonalizedCampaignMessageDialogComponent implements OnInit {
  @Input() currentJob?: any;

  @ViewChild(HtmlEditorComponent, { static: false }) htmlEditor: HtmlEditorComponent;
  @ViewChild('smsMessage', { static: false }) smsMessage: ElementRef;

  activeMessageType: string = '';

  currentMessageData: any = {
    variable_module: null,
    variable_field: null,
    variable: '',
    sms_text: '',
    email_html: '',
    email_subject: '',
    sms_panel_description: '',
    email_panel_description: '',
    personalized_bypass_7_day_rule: false,
    personalized_is_urgent: false,
  };

  // Used when empty messages.
  defaultSmsPanelDescription: string = 'Random SMS template will be used'; // A random SMS template will be used if this is left blank
  defaultEmailPanelDescription: string = 'Random Email template will be used';

  // Used when custom message is set.
  customSmsPanelDescription: string = 'Custom SMS Message Set';
  customEmailPanelDescription: string = 'Custom Email Message Set';

  smsSegmentInfo = {
    segmentsRequired: 0,
    encoding: '',
    nonGsmCharacters: null
  };

  modules: SsModule[] = [];
  filteredModules: SsModule[] = [];
  moduleFields: any[] = [];
  allowedModules: string[] = ['jobs', 'leads'];

  initialized: boolean = false;
  validMessageData: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<JobPersonalizedCampaignMessageDialogComponent>,
    private _moduleService: SsModuleService,
    public _sharedUtilsService: SharedUtilsService,
    private _matDialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.initDialogData();
  }


  async initDialogData() {
    if (this.currentJob) {
      if (this.currentJob.personalized_sms_text?.trim()?.length) {
        this.activeMessageType = 'sms';
        this.currentMessageData.sms_text = this.currentJob.personalized_sms_text;
        this.showSegmentInfo();
      }

      if (this.currentJob.personalized_email_text?.trim()?.length) {
        if (!this.activeMessageType?.length) this.activeMessageType = 'email'; // If not already set to sms above.
        this.currentMessageData.email_html = this.cleanHtmlForCkEditor(this.currentJob.personalized_email_text);
      }

      if (this.currentJob.personalized_email_subject?.trim()?.length) {
        this.currentMessageData.email_subject = this.currentJob.personalized_email_subject;
      } else {
        const jobTitle: string = this.currentJob?.name ?? this.currentJob?.profession ?? 'New Position';
        const formattedTitle: string = this._sharedUtilsService.titleCaseString(jobTitle);
        this.currentMessageData.email_subject = `Job Opportunity - ${formattedTitle.trim()}`;
      }

      this.currentMessageData.personalized_bypass_7_day_rule = !!this.currentJob.personalized_bypass_7_day_rule;
      this.currentMessageData.personalized_is_urgent = !!this.currentJob.personalized_is_urgent;
    }

    // console.log('this.currentJob: ', this.currentJob);

    await this.setModules();
    this.checkForm();
    this.initialized = true;
  }


  async setModules() {
    this.modules = await lastValueFrom(this._moduleService.getShallowModules()) ?? [];
    this.filteredModules = this.modules.filter((_m) => this.allowedModules.includes(_m?.name)); // Set only ones added in allowed modules.
    // console.log('Modules: ', this.modules);
    // console.log('Filtered Modules: ', this.filteredModules);

    await this.setupJobFields();
  }


  async setupJobFields() {
    // console.log('Setting up job fields');
    const jobModule = this.filteredModules?.find(_m => _m?.name === 'jobs') ?? null;
    // console.log('Job Module: ', jobModule);

    if (jobModule && jobModule != undefined) {
      this.currentMessageData.variable_module = jobModule;
      this.moduleFields = await this.prepareModuleFields(jobModule); // Get cloned and sorted fields with address parts added. Handles null or empty fields.
    }
    // console.log('Current Message Data: ', this.currentMessageData);
  }


  prepareModuleFields(_module): Promise<any[]> {
    return new Promise(async (resolve) => {
      if (!_module?.fields?.length) return resolve([]); // If no field length, just return empty array.

      const clonedFields = this._sharedUtilsService.deepClone(_module.fields); // Clone fields to avoid mutating original module.
      const fieldsWithAddressParts = await this._moduleService.addAddressPartFields(clonedFields, true); // Add address part fields.

      fieldsWithAddressParts.sort((a, b) => a.label?.localeCompare(b.label)); // Sort alphabetically by label.
      // console.log('Module Fields After Address Parts: ', fieldsWithAddressParts);

      resolve(fieldsWithAddressParts);
    });
  }


  async setNeededData(_type: string) {
    if (this.activeMessageType === _type) return; // Already active panel, do nothing.

    this.activeMessageType = _type; // Set current active panel.
    // console.log('Template Type: ', this.activeMessageType);

    // Type changed, so these shared fields should be reset.
    this.currentMessageData.variable_module = null;
    this.currentMessageData.variable_field = null;
    this.currentMessageData.variable = '';

    await this.setupJobFields(); // Reset model and fields for picker to job module.
    this.checkForm();
  }


  async showTemplatePicker() {
    if (!this.activeMessageType?.length) {
      this._sharedUtilsService.showErrorDialog('A type need to be set before selecting a template. Please set type first.');
      return;
    }

    const dialogRef = this._matDialog.open(ModuleSearchSelectComponent, { width: '95%', disableClose: true });
    const instance = dialogRef.componentInstance;
    instance.fieldToGetRelation = { type: { schemaType: { name: 'templates' } } };
    instance.multiSelectMode = false;
    instance.allowedSearchFields = ['name', 'profession', 'teams'];
    instance.defaultSearchTermsPassed = [
      { key: 'type', value: [this.activeMessageType] },
      { key: 'restrict_to_module', value: this.filteredModules }
    ];

    const _chosenSelection = await lastValueFrom(dialogRef.afterClosed());
    // console.log('_chosenSelection: ', _chosenSelection);

    if (_chosenSelection) {
      const templateSmsText: string = _chosenSelection?.body?.trim() ?? '';
      const templateEmailHtml: string = _chosenSelection?.body_html?.trim() ?? '';

      if (this.activeMessageType === 'sms') {
        if (templateSmsText?.length) {
          this.insertAtCursorTextArea(templateSmsText);
        } else {
          this._sharedUtilsService.showErrorDialog('There was no sms data to use from this template. Please select another.');
        }
      } else {
        if (templateEmailHtml?.length) {
          const cleanHtml = this.cleanHtmlForCkEditor(templateEmailHtml); // Need to remove invalid characters returned sometimes like '&nbsp;'.
          this.htmlEditor.addAtCursorWithContentCheck(cleanHtml);
          this.checkForm();
        } else {
          this._sharedUtilsService.showErrorDialog('There was no email data to use from this template. Please select another.');
        }
      }
    }
  }


  cleanHtmlForCkEditor(_input: string): string {
    let html = _input
      // Remove unsafe / unnecessary tags
      .replace(/<head[^>]*>.*?<\/head>/gis, '')
      .replace(/<meta[^>]*>/gis, '')
      .replace(/<style[^>]*>.*?<\/style>/gis, '')
      .replace(/<script[^>]*>.*?<\/script>/gis, '')
      .replace(/<xml[^>]*>.*?<\/xml>/gis, '')
      .replace(/<o:[^>]*>.*?<\/o:[^>]*>/gis, '')

      // Decode common HTML entities
      .replace(/&nbsp;/g, ' ')
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');

    // Collapse nested <p> tags (single regex pass)
    html = html.replace(/<p>(\s*<p>)+/gi, '<p>');
    html = html.replace(/(<\/p>\s*)+<\/p>/gi, '</p>');

    // Trim each paragraph individually
    html = html.replace(/<p>([\s\S]*?)<\/p>/gi, (_match, pContent) => `<p>${pContent.trim()}</p>`);

    return html.trim();
  }


  async updateModuleFields(_module) {
    // console.log({ _module });
    this.moduleFields = await this.prepareModuleFields(_module); // Get cloned and sorted fields with address parts added. Handles null or empty fields.
  }

  async updateVariable(_field) {
    // console.log({ _field });
    const moduleName = this.currentMessageData?.variable_module?.name || '';

    const variableName = '${' + `${moduleName}--${_field?.fieldName}` + '}';
    this.currentMessageData.variable = variableName;
    this.checkForm();
  }


  insertCurrentVariable() {
    // console.log('this.currentTemplate.type: ', this.activeMessageType);
    if (this.activeMessageType === 'sms') {
      this.insertAtCursorTextArea(this.currentMessageData.variable);
    } else {
      this.htmlEditor.addAtCursor(this.currentMessageData.variable);
      this.checkForm();
    }
  }


  insertAtCursorTextArea(_myValue: string) {
    const smsTextArea = this.smsMessage.nativeElement;

    if (smsTextArea.selectionStart || smsTextArea.selectionStart == '0') {
      var startPos = smsTextArea.selectionStart;
      var endPos = smsTextArea.selectionEnd;
      smsTextArea.value = smsTextArea.value.substring(0, startPos)
        + _myValue
        + smsTextArea.value.substring(endPos, smsTextArea.value.length);
    } else {
      smsTextArea.value += _myValue;
    }

    this.currentMessageData.sms_text = smsTextArea.value; // Manually update ngModel
    this.showSegmentInfo();
  }


  showSegmentInfo() {
    const messageBodyText = this.currentMessageData.sms_text + 'Firstname P EDU Healthcare';
    const segmentedMessage = new SegmentedMessage(messageBodyText);
    // console.log("segmentedMessage: ", segmentedMessage);

    // const isGsm = segmentedMessage.encodingName === 'GSM-7';
    // const numberOfSegments = segmentedMessage.segmentsCount;
    // const nonGsmChars = segmentedMessage.getNonGsmCharacters();
    // console.log('Is GSM encoded: ', isGsm);
    // console.log("Number of segments: ", numberOfSegments);
    // console.log('Non GSM Characters: ', nonGsmChars);

    this.smsSegmentInfo.encoding = segmentedMessage.encodingName;
    this.smsSegmentInfo.segmentsRequired = segmentedMessage.segmentsCount;
    this.smsSegmentInfo.nonGsmCharacters = segmentedMessage.getNonGsmCharacters();

    this.checkForm();
  }


  htmlEditorChanged(_updatedHtml: string) {
    // Don't set currentMessageData.email_html yet since unclean and user may still be editing.
    this.updatePanelDescriptions(); // Use htmlEditor.getHtml() inside to get current html.
  }


  checkForm() {
    let passesChecks = true;

    const smsText = this.currentMessageData?.sms_text?.trim() ?? '';

    if (smsText.length) {
      passesChecks = true;
      // Use smsSegmentInfo to validate GSM. Example to use for failing sms: Hello, this will fail € © 👍.
      // const encoding: string = this.smsSegmentInfo?.encoding ?? '';
      // const isGsm: boolean = encoding === 'GSM-7';
      // if (!isGsm) passesChecks = false; // Invalid if non-GSM characters found.

      // Optional: disable if over recommended segment length
      // const segmentsRequired: number = this.smsSegmentInfo?.segmentsRequired ?? 0;
      // if (!isGsm || segmentsRequired >= 5) passesChecks = false;
    }

    this.validMessageData = passesChecks;
    this.updatePanelDescriptions(); // Update the panel descriptions here since everything calls this.
  }


  updatePanelDescriptions() {
    // console.log('Current Message Data: ', this.currentMessageData);

    // Get values to check.
    const smsText = this.currentMessageData?.sms_text?.trim() ?? '';
    const editorHtml = this.htmlEditor?.myckeditor?.instance?.getData() ?? this.currentMessageData.email_html ?? ''; // If editor is present, see what is actually set. Fallback: One set on init, or empty string.
    // console.log('editorHtml: ', editorHtml);

    // Set correct panel description for both SMS amd Email.
    this.currentMessageData.sms_panel_description = smsText ? this.customSmsPanelDescription : this.defaultSmsPanelDescription;
    this.currentMessageData.email_panel_description = editorHtml ? this.customEmailPanelDescription : this.defaultEmailPanelDescription;
  }


  async saveMessage() {
    this.checkForm();

    // If not valid return now.
    if (!this.validMessageData) {
      this._sharedUtilsService.showErrorDialog('SMS message contains invalid characters. Please fix and try again.');
      return;
    }

    // THIS WILL HAPPEN IN BACKEND WHEN JOB IS PICKED UP

    // Use editor HTML if available, otherwise fall back to stored job value or empty string.
    const editorHtml = this.htmlEditor?.getHtml() ?? this.currentMessageData.email_html ?? '';
    this.currentMessageData.email_html = this.cleanHtmlForCkEditor(editorHtml); // Clean HTML before saving.

    // Close dialog and return both SMS and Email messages. If empty return null instead of empty strings.
    return this.dialogRef.close({
      wasSaved: true,
      sms_text: this.currentMessageData.sms_text?.trim() || null,
      email_html: this.currentMessageData.email_html?.trim() || null,
      email_subject:  this.currentMessageData.email_subject?.trim() || null,
      personalized_bypass_7_day_rule: !!this.currentMessageData.personalized_bypass_7_day_rule,
      personalized_is_urgent: !!this.currentMessageData.personalized_is_urgent
    });
  }


  cancel() {
    return this.dialogRef.close(null);
  }
}